import React from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';

/**
 * UserRow - Individual row component for users table
 * 
 * @param {Object} props - Component props
 * @param {Object} props.user - User data object
 * @param {string} props.user.id - User ID
 * @param {string} props.user.fullName - User's full name
 * @param {string} props.user.email - User's email address
 * @param {string} [props.user.title] - User's title
 * @param {string} [props.user.company] - User's company
 * @param {boolean} props.user.isSuperAdmin - Whether user is super admin
 * @param {Array} props.user.organizations - Array of user's organizations
 * @param {number} props.index - Row index for border styling
 * @param {number} props.totalRows - Total number of rows for border styling
 * @param {string|null} props.processingAction - ID of user currently being processed
 * @param {Function} props.onToggleAdmin - Called when admin checkbox is toggled
 * @param {Function} props.onDeleteUser - Called when delete button is clicked
 * @returns {React.ReactElement} User row component
 */
const UserRow = ({
  user,
  index,
  totalRows,
  processingAction,
  onToggleAdmin,
  onDeleteUser,
  selected,
  onSelect
}) => {
  const isActionPending = processingAction === user.id;

  return (
    <tr 
      key={user.id} 
      onClick={() => onSelect?.(user.id)}
      style={{ 
        borderBottom: index < totalRows - 1 ? '1px solid var(--border)' : 'none', 
        transition: 'var(--transition)',
        cursor: onSelect ? 'pointer' : undefined,
        background: selected ? 'var(--bg-secondary)' : undefined
      }} 
      className="table-row-hover"
    >
      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600 }}>
        {user.fullName || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Not specified</span>}
      </td>
      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
        {user.email}
      </td>
      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
        {user.title && <div>{user.title}</div>}
        {user.company && <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>{user.company}</div>}
        {!user.title && !user.company && '-'}
      </td>
      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {user.organizations.length > 0 ? user.organizations.map(ut => (
            <span 
              key={ut.id} 
              style={{
                padding: '2px 8px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: 'var(--text-primary)'
              }} 
              title={ut.id}
            >
              {ut.name}
            </span>
          )) : <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>None</span>}
        </div>
      </td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
        {isActionPending ? (
          <LoadingSpinner size="small" style={{ margin: '0 auto' }} />
        ) : (
          <input 
            type="checkbox"
            checked={user.isSuperAdmin}
            onClick={e => e.stopPropagation()}
            onChange={() => onToggleAdmin(user.id, user.fullName || user.email)}
            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: 'var(--accent)' }}
          />
        )}
      </td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
        <button 
          disabled={isActionPending}
          onClick={(e) => { e.stopPropagation(); onDeleteUser(user.id, user.email); }}
          className="family-menu-trigger delete" 
          style={{ 
            padding: '4px 8px', 
            color: 'var(--error)', 
            background: 'transparent', 
            border: 'none', 
            cursor: 'pointer', 
            borderRadius: '4px', 
            transition: 'var(--transition)' 
          }}
          title="Delete User Account"
        >
          <i className="fa-solid fa-trash-can"></i>
        </button>
      </td>
    </tr>
  );
};

export default UserRow;